import { LibrarySkeleton } from '@/components/Library/LibrarySkeleton';
import OrgVideosDropList from '@/components/Library/OrgVideosDropList';
import { LoadingSkeletons } from '@/components/LoadingSkeletons/LoadingSkeletons';
import NoResults from '@/components/NoSearchResults/NoResults';
import { VIDEO_TILE_WIDTH } from '@/components/VideoTile';
import { useBlockBrowserClose } from '@/hooks/useBlockBrowserClose';
import { translationVideosSearch } from '@/pages/translation/state';
import { GetTranslationGroupsRes } from '@/services/api';
import { useFlag } from '@/services/featureFlags';
import { ITranslationGroup, TranslationGroupStatus } from '@d-id/types';
import styled from '@emotion/styled';
import { forwardRef, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { SKELETON_COUNT, VIDEO_CONTAINER_MAX_WIDTH, VIDEO_CONTAINER_MIN_WIDTH } from '../../constants';
import { DeleteTranslationGroupModal } from '../DeleteTranslationGroupModal';
import { DeleteTranslationModal } from '../DeleteTranslationModal';
import { TranslationGroupCard } from '../TranslationGroupCard/TranslationGroupCard';
import { EXAMPLE_TRANSLATION_GROUPS } from './example-translations';

const LibraryContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const SectionTitle = styled.h2`
    color: ${({ theme }) => theme.colors.black[700]};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.32px;
`;

const SectionTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 8px;
`;

const VideoContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(${VIDEO_TILE_WIDTH}px, 1fr));
    grid-row: 1; /* Ensure each row takes up equal height */
    grid-gap: 24px;
    overflow: hidden;
    justify-content: space-between;
`;

interface Props {
    isLoading: boolean;
    isFetchingNextPage: boolean;
    hasNextPage?: boolean;
    fetchNextPage: () => void;
    data?: GetTranslationGroupsRes;
}

const TranslationsEndDiv = forwardRef<HTMLDivElement, { show: boolean }>(({ show }, ref) => {
    if (!show) {
        return null;
    }

    return (
        <div ref={ref}>
            <LoadingSkeletons
                count={SKELETON_COUNT}
                skeletonMaxWidth={VIDEO_CONTAINER_MAX_WIDTH}
                containerMinWidth={VIDEO_CONTAINER_MIN_WIDTH}
            />
        </div>
    );
});

function shouldShowGroup(group: ITranslationGroup, filters: { createdBy?: string; search?: string }) {
    const createdByFilter = group.created_by.includes(filters.createdBy || '');
    const searchFilter = !filters.search?.trim() || group.name.toLowerCase().includes(filters.search.toLowerCase());

    return createdByFilter && searchFilter;
}

export const TranslationLibrary = ({ data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage }: Props) => {
    const translationExampleVideosEnabled = useFlag('translation_example_videos_enabled', false);
    const [search, setSearch] = useRecoilState(translationVideosSearch);
    const [searchParams] = useSearchParams();

    const createdBy = useMemo(() => searchParams.get('created_by') as string, [searchParams]);

    const isUploading = useMemo(
        () => !!data?.groups.some(group => [TranslationGroupStatus.Uploading, TranslationGroupStatus.Validating].includes(group.status)),
        [data]
    );
    const { ref, inView } = useInView();
    const filteredTranslationGroups = useMemo(
        () => data?.groups?.filter(group => shouldShowGroup(group, { createdBy, search })) || [],
        [data, search, createdBy]
    );

    useBlockBrowserClose(() => isUploading);

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    if (isLoading) {
        return <LibrarySkeleton amount={9} tileHeight="190px" tileWidth={`${VIDEO_TILE_WIDTH}px`} />;
    }

    if (!data?.groups.length && !translationExampleVideosEnabled) {
        return null;
    }

    if (search && !filteredTranslationGroups.length) {
        return <NoResults show handleClearSearch={() => setSearch('')} />;
    }

    return (
        <LibraryContainer>
            <SectionTitleContainer>
                <SectionTitle>Recent translated videos</SectionTitle>
                <OrgVideosDropList />
            </SectionTitleContainer>
            <VideoContainer>
                {filteredTranslationGroups.map(group => (
                    <TranslationGroupCard translationGroup={group} key={group.id} origin="video-translate" />
                ))}
                {translationExampleVideosEnabled &&
                    EXAMPLE_TRANSLATION_GROUPS.map(group => (
                        <TranslationGroupCard translationGroup={group} key={group.id} isExample origin="video-translate" />
                    ))}
                <DeleteTranslationGroupModal />
                <DeleteTranslationModal />
            </VideoContainer>
            <TranslationsEndDiv ref={ref} show={!!(hasNextPage && data?.groups.length)} />
        </LibraryContainer>
    );
};
